import type { SingleValue } from 'react-select';

import { Select } from '@revelio/ui';

import type { FilterModifier } from '../../datasets/deliverable-column-config';
import { customFilterSelectCssVars } from './disabled-input-overrides';

type ModifierOption = {
  label: string;
  value: string;
};

type FilterModifierSelectProps = {
  modifiers: FilterModifier[];
  value: string | null;
  onChange: (key: string) => void;
  isDisabled: boolean;
  testId?: string;
};

function toModifierOptions(modifiers: FilterModifier[]): ModifierOption[] {
  return modifiers.map((modifier) => ({
    label: modifier.name,
    value: modifier.key,
  }));
}

function getSelectedOption(
  options: ModifierOption[],
  value: string | null
): ModifierOption | null {
  if (options.length === 0) {
    return null;
  }

  if (value === null) {
    return options[0] ?? null;
  }

  const matchedOption = options.find((option) => option.value === value);
  return matchedOption ?? options[0] ?? null;
}

export function FilterModifierSelect({
  modifiers,
  value,
  onChange,
  isDisabled,
  testId,
}: FilterModifierSelectProps) {
  const options = toModifierOptions(modifiers);

  if (options.length === 0) {
    return null;
  }

  return (
    <Select<ModifierOption>
      value={getSelectedOption(options, value)}
      options={options}
      onChange={(option: SingleValue<ModifierOption>) => {
        if (option !== null) {
          onChange(option.value);
        }
      }}
      blurInputOnSelect={false}
      isSearchable={false}
      isDisabled={isDisabled}
      size="sm"
      cssVars={customFilterSelectCssVars}
      data-testid={testId}
    />
  );
}
